import React from 'react';
import PremiumPageLayout from '../components/PremiumPageLayout';
import { Target, Users, Award, Zap, Heart, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Job-Ready Focus',
      description: 'We don\'t just teach theory. Every lesson, lab, and project is designed to prepare you for real IT roles.'
    },
    {
      icon: Users,
      title: 'Mentor Support',
      description: 'Learn from IT professionals who\'ve been in your shoes. Get 1:1 guidance, code reviews, and career advice.'
    },
    {
      icon: Award,
      title: 'Hands-On Learning',
      description: 'Build a portfolio of 3-5 real projects. Practice in live cloud labs. Graduate with proven, demonstrable skills.'
    },
    {
      icon: Zap,
      title: 'Fast Track',
      description: '12 weeks to job-ready. No wasted time on irrelevant content. Focus on what actually gets you hired.'
    },
    {
      icon: Heart,
      title: 'Supportive Community',
      description: 'Join a community of learners and graduates. Network, collaborate, and support each other\'s growth.'
    },
    {
      icon: TrendingUp,
      title: 'Career Growth',
      description: 'Start with IT Support ($65K-$85K), advance to Cloud/DevOps ($90K-$120K), specialize in AI, security, or data.'
    }
  ];

  return (
    <PremiumPageLayout>
      <div className="relative py-24 px-4 overflow-hidden">
        <div className="max-w-[1200px] mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/5">
              <span className="text-sm font-semibold text-[#06B6D4]">About CareerLift</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              We Help Beginners Launch Real IT Careers
            </h1>
            <p className="text-xl text-[#C7D2FE] max-w-3xl mx-auto">
              CareerLift was founded on a simple belief: anyone can learn IT skills and land a great job — they just need the right training, mentorship, and support.
            </p>
          </div>

          {/* Mission Statement */}
          <div 
            className="rounded-[24px] p-12 mb-16 border border-white/10 backdrop-blur-md"
            style={{ 
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(6, 182, 212, 0.04) 100%)',
              boxShadow: '0 22px 65px rgba(15, 23, 42, 0.40), inset 0 1px 0 0 rgba(255,255,255,0.1)'
            }}
          >
            <h2 className="text-3xl font-bold text-white mb-6 text-center" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              Our Mission
            </h2>
            <p className="text-lg text-[#C7D2FE] leading-relaxed text-center max-w-4xl mx-auto">
              To bridge the gap between education and employment. Most IT courses teach theory but leave graduates unprepared for real jobs. We focus on hands-on skills, portfolio projects, and interview preparation — so you can confidently walk into any IT interview and land the role.
            </p>
          </div>

          {/* Values Grid */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-12 text-center" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              What Makes Us Different
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-[20px] border border-white/10 backdrop-blur-sm hover:-translate-y-2 transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(37, 99, 235, 0.03) 100%)' }}
                >
                  <div className="inline-flex p-3 rounded-full bg-[#6366F1]/10 mb-4">
                    <value.icon size={28} className="text-[#6366F1]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-[#C7D2FE] leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Section */}
          <div 
            className="rounded-[24px] p-12 border border-white/10 backdrop-blur-md"
            style={{ 
              background: 'linear-gradient(135deg, rgba(16, 163, 74, 0.08) 0%, rgba(6, 182, 212, 0.04) 100%)',
              boxShadow: '0 22px 65px rgba(15, 23, 42, 0.40), inset 0 1px 0 0 rgba(255,255,255,0.1)'
            }}
          >
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-white mb-2">500+</div>
                <div className="text-[#C7D2FE]">Graduates</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">85%</div>
                <div className="text-[#C7D2FE]">Job Placement Rate</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-white mb-2">$78K</div>
                <div className="text-[#C7D2FE]">Average Starting Salary</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Glows */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#06B6D4]/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#6366F1]/10 rounded-full blur-[120px] pointer-events-none"></div>
      </div>
    </PremiumPageLayout>
  );
};

export default AboutPage;
